// Real Firebase Requests & Voting Service

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc as firestoreDeleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { filterProfanity } from '../utils/profanityFilter';
import { getUpvotesForGroup, makePPGroupKey } from './nominationPool';
import { normalizeSongIdentity, spotifyTrackIdFrom, type CatalogIds, type SongSource } from '../utils/songIdentity';
import { linkFitsPlayMode, playModeOf, songFitsPlayMode, type PlayModeConfig } from './playModes';
import { detectPlatformFromUrl } from '../utils/urlUtils';
import { canonicalLinkOrSame } from '../utils/songLinks';
import { effectiveBallot } from '../utils/rankingOrder';
import { playNextManualOrder } from '../utils/manualQueuePlacement';

// ============================================
// STRING MATCHING HELPERS (pure functions, no DB)
// ============================================

function stripFeaturedArtists(text: string): string {
  return text
    .replace(/\s+(?:&|feat\.?|ft\.?|and)\s+.+$/i, '')
    .replace(/,\s+.+$/, '');
}

export function normalizeForComparison(text: string): string {
  return stripFeaturedArtists(text)
    .toLowerCase()
    .replace(/\s+[-–—]\s+.+$/, '')
    .replace(/\(.*?\)/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/[-–—]/g, ' ')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function stringSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  if (longer.length === 0) return 1.0;

  const costs: number[] = [];
  for (let i = 0; i <= str1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= str2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[str2.length] = lastValue;
  }
  const distance = costs[str2.length];
  return (longer.length - distance) / longer.length;
}

export function isFuzzySongMatch(
  title1: string, artist1: string, title2: string, artist2: string, threshold = 0.8
): boolean {
  const normTitle1 = normalizeForComparison(title1);
  const normTitle2 = normalizeForComparison(title2);
  const normArtist1 = normalizeForComparison(artist1);
  const normArtist2 = normalizeForComparison(artist2);

  if (normTitle1 === normTitle2 && normArtist1 === normArtist2) return true;

  if (normTitle1.includes(normTitle2) || normTitle2.includes(normTitle1)) {
    if (stringSimilarity(normArtist1, normArtist2) >= threshold) return true;
  }

  return stringSimilarity(normTitle1, normTitle2) >= threshold &&
    stringSimilarity(normArtist1, normArtist2) >= threshold;
}

// ============================================
// TYPES
// ============================================

export interface RallyTrend {
  outShare: number;
  extremeShare: number;
  direction: 'closer' | 'slipping' | null;
  flipCount: number;
  almostThereSent: boolean;
  outAt: string;
  downvoteCount: number;
  participantCount: number;
}

export type ReviewHolder = 'dj' | 'coApprovalDj';

export interface ReviewHold {
  holder: ReviewHolder;
  name: string | null;
  untilMs: number;
}

export interface ReviewStamp {
  kind: 'coApprovalDj';
  name: string;
  at: string;
}

function reviewHoldFrom(value: unknown): ReviewHold | null {
  if (typeof value !== 'object' || value === null) return null;
  const { holder, name, untilMs } = value as Record<string, unknown>;
  if ((holder !== 'dj' && holder !== 'coApprovalDj') || typeof untilMs !== 'number') return null;
  return { holder, name: typeof name === 'string' ? name : null, untilMs };
}

function reviewStampFrom(value: unknown): ReviewStamp | null {
  if (typeof value !== 'object' || value === null) return null;
  const { kind, name, at } = value as Record<string, unknown>;
  if (kind !== 'coApprovalDj' || typeof name !== 'string' || typeof at !== 'string') return null;
  return { kind, name, at };
}

export interface SongRequest {
  id: string;
  eventId: string;
  song: {
    title: string;
    artist: string;
    albumArtUrl?: string;
    durationMs?: number;
    explicitFlag: boolean;
    itunesTrackId?: string;
    previewUrl?: string;
    source?: SongSource;
    catalogIds?: CatalogIds;
    isrc?: string | null;
  };
  requestedBy: {
    oderId: string;
    nickname?: string;
  };
  message?: string;
  notes?: string;
  status: 'pending' | 'queued' | 'nowPlaying' | 'played' | 'rejected' | 'overflow';
  rejectionReason?: string;
  voteCount: number;
  upvoteCount: number;
  downvoteCount: number;
  votedOut: boolean;
  crowdFavorite?: boolean;
  rallyTrend?: RallyTrend | null;
  manualOrder?: number;
  lockedNext?: boolean;   // Position is locked — cannot be displaced by votes/drag
  queueLockedAt?: string;  // set when Queue Next created the lock (ITER-171)
  manualPickAt?: string;   // set when the DJ picked this Now Playing by hand (ITER-173)
  unplannedPlayAt?: string;  // set on a Let it play song that wasn't in the queue (ITER-173)
  playerTrack?: { title: string; artist: string };  // the player's own title/artist for this Now Playing song (ITER-182)
  bridgeRecognizedAt?: string;  // Bridge-stamped when its match moved the song into Now Playing (ITER-172)
  bridgeCleanWhileFlagged?: number;  // server-stamped clean recognitions while flagged (ITER-172)
  djNotes?: string;
  preppedInSpotify: boolean;
  queuedInPlayer?: boolean;  // DJ has queued this song in their music player
  /** @deprecated use queuedInPlayer */
  queuedInSpotify?: boolean;
  spotifyUri?: string;
  // Platform matching (replaces Spotify-only fields for non-Spotify DJs)
  platformLink?: string;   // Direct link to this song on the DJ's platform
  platformName?: string;   // e.g. 'Spotify', 'Apple Music', 'YouTube Music'
  songLinkUrl?: string;    // Universal song.link URL
  durationVerified?: 'exact' | 'approximate' | null; // DJ duration verification status
  linkLibraryKey?: string;       // server-stamped songLinks key the link came from (ITER-139)
  linkUseCountedAt?: string;     // server-stamped when Played counted a clean use
  wrongSongReportedAt?: string;  // server-stamped by reportWrongSong
  wrongSongFixedAt?: string;     // DJ write when a fix is pasted
  contested?: boolean;
  superRequest?: boolean;
  superRequestCount?: number;
  poolApproved?: boolean;
  // Local Files
  localFile?: {
    storageUrl: string;
    fileName: string;
    durationMs: number;
    temporary: boolean;
  };
  visibleToAttendees?: boolean;
  includeInPlaylist?: boolean;
  // Co-Approval
  coApprovalStatus?: 'pending' | 'approved' | 'rejected';
  coApprovalReason?: string;
  // Co-approval DJ (ITER-194): server-written only
  reviewHold?: ReviewHold | null;
  reviewedBy?: ReviewStamp | null;
  // VIP Trigger
  vipTriggerable?: boolean;
  vipOneTime?: boolean;
  vipTriggered?: boolean;
  vipPriority?: boolean;
  // Practice Mode
  practiceMode?: boolean;
  // Entry source tracking
  entrySource?: 'auto-matched' | 'imported' | 'pending-approved' | 'dj-added';
  matchVerified?: boolean;
  isOverflow?: boolean;
  overflowPriority?: number;
  prizeSkipTheLine?: boolean;
  prizeId?: string;
  cancelledByAttendee?: boolean;
  cancelledAt?: string;
  timestamps: {
    requested: string;
    approved?: string;
    startedPlaying?: string;
    finishedPlaying?: string;
    rejected?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AttendeeRanking {
  attendeeId: string;
  eventId: string;
  rankings: string[];
  fullOrder?: string[];
  sortedThroughId?: string | null;
  updatedAt: string;
}

export interface Voter {
  attendeeId: string;
  nickname?: string;
}

// ============================================
// HELPERS
// ============================================

function playerTrackOf(raw: unknown): { title: string; artist: string } | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { title, artist } = raw as { title?: unknown; artist?: unknown };
  if (typeof title !== 'string' || title.trim() === '' || typeof artist !== 'string') return undefined;
  return { title, artist };
}

export function rallyTrendOf(raw: unknown): RallyTrend | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const trend = raw as Record<string, unknown>;
  if (typeof trend.outShare !== 'number' || typeof trend.extremeShare !== 'number') return null;
  if (trend.direction !== null && trend.direction !== 'closer' && trend.direction !== 'slipping') return null;
  if (typeof trend.flipCount !== 'number' || typeof trend.almostThereSent !== 'boolean') return null;
  if (typeof trend.outAt !== 'string') return null;
  return {
    outShare: trend.outShare,
    extremeShare: trend.extremeShare,
    direction: trend.direction,
    flipCount: trend.flipCount,
    almostThereSent: trend.almostThereSent,
    outAt: trend.outAt,
    downvoteCount: typeof trend.downvoteCount === 'number' ? trend.downvoteCount : 0,
    participantCount: typeof trend.participantCount === 'number' ? trend.participantCount : 0,
  };
}

export function docToRequest(docSnap: any): SongRequest {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    song: data.song,
    requestedBy: data.requestedBy,
    message: data.message || undefined,
    notes: data.notes || undefined,
    status: data.status,
    rejectionReason: data.rejectionReason || undefined,
    voteCount: data.voteCount || 0,
    upvoteCount: data.upvoteCount || 0,
    downvoteCount: data.downvoteCount || 0,
    votedOut: data.votedOut || false,
    crowdFavorite: data.crowdFavorite === true,
    rallyTrend: rallyTrendOf(data.rallyTrend),
    manualOrder: typeof data.manualOrder === 'number' ? data.manualOrder : undefined,
    lockedNext: data.lockedNext || undefined,
    queueLockedAt: typeof data.queueLockedAt === 'string' ? data.queueLockedAt : undefined,
    manualPickAt: typeof data.manualPickAt === 'string' ? data.manualPickAt : undefined,
    unplannedPlayAt: typeof data.unplannedPlayAt === 'string' ? data.unplannedPlayAt : undefined,
    playerTrack: playerTrackOf(data.playerTrack),
    bridgeRecognizedAt: typeof data.bridgeRecognizedAt === 'string' ? data.bridgeRecognizedAt : undefined,
    bridgeCleanWhileFlagged:
      typeof data.bridgeCleanWhileFlagged === 'number' && Number.isFinite(data.bridgeCleanWhileFlagged)
        ? data.bridgeCleanWhileFlagged
        : undefined,
    djNotes: data.djNotes || undefined,
    preppedInSpotify: data.preppedInSpotify || false,
    queuedInSpotify: data.queuedInSpotify || false,
    spotifyUri: data.spotifyUri || undefined,
    platformLink: data.platformLink || undefined,
    platformName: data.platformName || undefined,
    songLinkUrl: data.songLinkUrl || undefined,
    durationVerified: data.durationVerified || undefined,
    queuedInPlayer: data.queuedInPlayer || undefined,
    linkLibraryKey: typeof data.linkLibraryKey === 'string' ? data.linkLibraryKey : undefined,
    linkUseCountedAt: typeof data.linkUseCountedAt === 'string' ? data.linkUseCountedAt : undefined,
    wrongSongReportedAt: typeof data.wrongSongReportedAt === 'string' ? data.wrongSongReportedAt : undefined,
    wrongSongFixedAt: typeof data.wrongSongFixedAt === 'string' ? data.wrongSongFixedAt : undefined,
    contested: data.contested || false,
    superRequest: data.superRequest || false,
    superRequestCount: data.superRequestCount || undefined,
    poolApproved: data.poolApproved || false,
    localFile: data.localFile || undefined,
    visibleToAttendees: data.visibleToAttendees ?? true,
    includeInPlaylist: data.includeInPlaylist ?? true,
    coApprovalStatus: data.coApprovalStatus || undefined,
    coApprovalReason: data.coApprovalReason || undefined,
    reviewHold: reviewHoldFrom(data.reviewHold),
    reviewedBy: reviewStampFrom(data.reviewedBy),
    vipTriggerable: data.vipTriggerable || false,
    vipOneTime: data.vipOneTime || false,
    vipTriggered: data.vipTriggered || false,
    vipPriority: data.vipPriority || false,
    entrySource: data.entrySource || undefined,
    matchVerified: data.matchVerified ?? undefined,
    isOverflow: data.isOverflow || false,
    overflowPriority: data.overflowPriority ?? undefined,
    prizeSkipTheLine: data.prizeSkipTheLine || undefined,
    prizeId: data.prizeId || undefined,
    cancelledByAttendee: data.cancelledByAttendee === true,
    cancelledAt: data.cancelledAt ?? undefined,
    practiceMode: data.practiceMode === true || undefined,
    timestamps: data.timestamps || { requested: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt },
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
}

// Trending config
const TRENDING_WINDOW_MS = 5 * 60 * 1000;
const TRENDING_VOTE_THRESHOLD = 3;

// ============================================
// BASIC CRUD
// ============================================

export function getAllRequests(): SongRequest[] {
  return []; // Not used with real Firebase
}

export async function getRequestsByEventId(eventId: string): Promise<SongRequest[]> {
  const q = query(collection(db, 'requests'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToRequest);
}

export async function getNowPlaying(eventId: string): Promise<SongRequest | null> {
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('status', '==', 'nowPlaying')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return docToRequest(snapshot.docs[0]);
}

export function subscribeToNowPlaying(
  eventId: string,
  callback: (nowPlaying: SongRequest | null) => void
): Unsubscribe {
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('status', '==', 'nowPlaying')
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.empty ? null : docToRequest(snapshot.docs[0]));
  });
}

/**
 * Real-time listener for all requests in an event.
 * After the initial snapshot, only changed documents are transferred (delta reads).
 */
export function subscribeToRequestsByEventId(
  eventId: string,
  callback: (requests: SongRequest[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(collection(db, 'requests'), where('eventId', '==', eventId));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToRequest));
  }, onError);
}

export async function getRequestById(requestId: string): Promise<SongRequest | null> {
  const docSnap = await getDoc(doc(db, 'requests', requestId));
  if (!docSnap.exists()) return null;
  return docToRequest(docSnap);
}

export async function createRequest(
  eventId: string,
  data: {
    song: SongRequest['song'];
    requestedBy: SongRequest['requestedBy'];
    message?: string;
    notes?: string;
    initialStatus?: 'pending' | 'queued' | 'overflow';
    contested?: boolean;
    superRequest?: boolean;
    platformLink?: string;
    platformName?: string;
    songLinkUrl?: string;
    spotifyUri?: string;
    durationVerified?: 'exact' | 'approximate' | null;
    entrySource?: SongRequest['entrySource'];
    matchVerified?: boolean;
    poolApproved?: boolean;
    isOverflow?: boolean;
    overflowPriority?: number;
    prizeSkipTheLine?: boolean;
    prizeId?: string;
    isLocalFile?: boolean;
    unplannedPlayAt?: string;
    manualOrder?: number;
  }
): Promise<SongRequest> {
  const now = new Date().toISOString();
  const song: SongRequest['song'] = {
    ...data.song,
    ...normalizeSongIdentity({
      song: data.song,
      platformLink: data.platformLink,
      platformName: data.platformName,
      spotifyUri: data.spotifyUri,
      isLocalFile: data.isLocalFile,
    }),
  };
  // Sanitize: Firestore rejects undefined values, replace with null
  const sanitize = (obj: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === undefined ? null : v]));

  const status = data.initialStatus || 'pending';

  // Pool cap: reject nomination if pool is already full
  if (data.superRequest) {
    const existing = await getRequestsByEventId(eventId);
    const poolSize = existing.filter(r => r.superRequest && r.poolApproved && r.status === 'pending').length;
    if (poolSize >= 50) {
      throw new Error("The People's Pick pool is full (50 nominations max). Try again later.");
    }
  }

  // Filter nickname for profanity and enforce length limit
  const filteredRequestedBy = {
    ...data.requestedBy,
    ...(data.requestedBy.nickname
      ? { nickname: filterProfanity(data.requestedBy.nickname.slice(0, 30)) }
      : {}),
  };
  const docRef = await addDoc(collection(db, 'requests'), {
    eventId,
    song: sanitize(song as unknown as Record<string, unknown>),
    requestedBy: sanitize(filteredRequestedBy as unknown as Record<string, unknown>),
    message: data.message ? filterProfanity(data.message.slice(0, 500)) : null,
    notes: data.notes ? filterProfanity(data.notes.slice(0, 500)) : null,
    status,
    voteCount: 0,
    upvoteCount: 0,
    downvoteCount: 0,
    votedOut: false,
    manualOrder: data.prizeSkipTheLine ? playNextManualOrder() : (data.manualOrder ?? null),
    djNotes: null,
    preppedInSpotify: false,
    contested: data.contested || false,
    superRequest: data.superRequest || false,
    superRequestCount: 0,
    poolApproved: data.poolApproved ?? false,
    ...(data.platformLink ? { platformLink: data.platformLink } : {}),
    ...(data.platformName ? { platformName: data.platformName } : {}),
    ...(data.songLinkUrl ? { songLinkUrl: data.songLinkUrl } : {}),
    ...(data.spotifyUri ? { spotifyUri: data.spotifyUri } : {}),
    ...(data.durationVerified ? { durationVerified: data.durationVerified } : {}),
    ...(data.entrySource ? { entrySource: data.entrySource } : {}),
    ...(data.matchVerified !== undefined ? { matchVerified: data.matchVerified } : {}),
    ...(data.isOverflow ? { isOverflow: true } : {}),
    ...(data.overflowPriority !== undefined ? { overflowPriority: data.overflowPriority } : {}),
    ...(data.prizeSkipTheLine ? { prizeSkipTheLine: true, prizeId: data.prizeId || null } : {}),
    ...(data.unplannedPlayAt ? { unplannedPlayAt: data.unplannedPlayAt } : {}),
    timestamps: { requested: now },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    eventId,
    song,
    requestedBy: data.requestedBy,
    message: data.message ? filterProfanity(data.message) : undefined,
    notes: data.notes ? filterProfanity(data.notes) : undefined,
    status,
    voteCount: 0,
    upvoteCount: 0,
    downvoteCount: 0,
    votedOut: false,
    preppedInSpotify: false,
    contested: data.contested || false,
    superRequest: data.superRequest || false,
    superRequestCount: 0,
    poolApproved: data.poolApproved ?? false,
    isOverflow: data.isOverflow || false,
    overflowPriority: data.overflowPriority,
    ...(data.entrySource ? { entrySource: data.entrySource } : {}),
    ...(data.unplannedPlayAt ? { unplannedPlayAt: data.unplannedPlayAt } : {}),
    timestamps: { requested: now },
    createdAt: now,
    updatedAt: now,
  };
}

export interface MatchedLinkFields {
  platformLink: string;
  platformName: string;
  songLinkUrl?: string;
  spotifyUri?: string;
  song: SongRequest['song'];
}

export function matchedLinkFields(
  link: { platformLink: string; platformName: string; songLinkUrl?: string | null },
  song: SongRequest['song'],
): MatchedLinkFields {
  const spotifyId = spotifyTrackIdFrom(link.platformLink);
  const identity = normalizeSongIdentity({
    song,
    platformLink: link.platformLink,
    platformName: link.platformName,
  });
  return {
    platformLink: canonicalLinkOrSame(link.platformLink),
    platformName: link.platformName,
    ...(link.songLinkUrl ? { songLinkUrl: link.songLinkUrl } : {}),
    ...(spotifyId ? { spotifyUri: `spotify:track:${spotifyId}` } : {}),
    song: { ...song, catalogIds: identity.catalogIds, source: identity.source, isrc: identity.isrc },
  };
}

export async function updateRequest(
  requestId: string,
  data: Partial<Omit<SongRequest, 'id' | 'eventId' | 'createdAt'>>
): Promise<SongRequest> {
  const ref = doc(db, 'requests', requestId);
  // Sanitize: Firestore rejects undefined values, replace with null
  const sanitized = Object.fromEntries(
    Object.entries(data).map(([k, v]) => {
      if (v === undefined) return [k, null];
      if (v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
        const nested = Object.fromEntries(
          Object.entries(v as Record<string, unknown>).map(([nk, nv]) => [nk, nv === undefined ? null : nv])
        );
        return [k, nested];
      }
      return [k, v];
    })
  );
  await updateDoc(ref, { ...sanitized, updatedAt: serverTimestamp() });
  const updated = await getDoc(ref);
  return docToRequest(updated);
}

export async function deleteRequest(requestId: string): Promise<void> {
  // Delete votes for this request
  const votesSnap = await getDocs(query(collection(db, 'votes'), where('requestId', '==', requestId)));
  const batch = writeBatch(db);
  votesSnap.docs.forEach(v => batch.delete(v.ref));
  batch.delete(doc(db, 'requests', requestId));
  await batch.commit();
}

export async function deleteRequestsByEventId(eventId: string): Promise<void> {
  const requests = await getRequestsByEventId(eventId);
  const batch = writeBatch(db);
  for (const req of requests) {
    const votesSnap = await getDocs(query(collection(db, 'votes'), where('requestId', '==', req.id)));
    votesSnap.docs.forEach(v => batch.delete(v.ref));
    batch.delete(doc(db, 'requests', req.id));
  }
  await batch.commit();
}

export async function deleteRequestsByEventIds(eventIds: string[]): Promise<void> {
  for (const eventId of eventIds) {
    await deleteRequestsByEventId(eventId);
  }
}

export function getRequestsStorageKey(): string {
  return 'requests';
}

// ============================================
// IMPORT FROM PAST EVENT
// ============================================

/** @deprecated Use SmartImportResult instead */
export interface ImportResult {
  imported: number;
  skippedDuplicates: number;
  total: number;
}

export interface SmartImportResult {
  importedToQueue: number;
  importedToPending: number;
  skippedDuplicates: number;
  total: number;
  unmatchedRequestIds: string[];
}

export async function importSongsFromPastEvent(
  targetEventId: string,
  sourceRequests: SongRequest[],
  djUid: string,
  mode: PlayModeConfig,
): Promise<SmartImportResult> {
  let importedToQueue = 0;
  let importedToPending = 0;
  let skippedDuplicates = 0;
  const unmatchedRequestIds: string[] = [];

  // Pre-fetch all current requests for the target event once (for duplicate detection)
  const currentRequests = await getRequestsByEventId(targetEventId);

  for (const sourceReq of sourceRequests) {
    // Check for duplicates using fuzzy matching against current requests
    const isDuplicate = currentRequests.some(existing =>
      isFuzzySongMatch(sourceReq.song.title, sourceReq.song.artist, existing.song.title, existing.song.artist)
    );

    if (isDuplicate) {
      skippedDuplicates++;
      continue;
    }

    const fits = songFitsPlayMode(sourceReq, mode);
    const linkFits = fits && linkFitsPlayMode(sourceReq.platformLink, sourceReq.platformName, mode);
    const spotifyUriFits = fits && mode.provider === 'spotify' && !!sourceReq.spotifyUri;
    const songWithIdentity: SongRequest['song'] = {
      ...sourceReq.song,
      ...normalizeSongIdentity({
        song: sourceReq.song,
        platformLink: sourceReq.platformLink,
        platformName: sourceReq.platformName,
        spotifyUri: sourceReq.spotifyUri,
        isLocalFile: !!sourceReq.localFile,
      }),
    };

    const newReq = await createRequest(targetEventId, {
      song: songWithIdentity,
      requestedBy: { oderId: djUid, nickname: 'DJ Import' },
      initialStatus: fits ? 'queued' : 'pending',
      ...(linkFits && sourceReq.platformLink ? { platformLink: sourceReq.platformLink } : {}),
      ...(linkFits && sourceReq.platformName ? { platformName: sourceReq.platformName } : {}),
      ...(linkFits && sourceReq.songLinkUrl ? { songLinkUrl: sourceReq.songLinkUrl } : {}),
      ...(spotifyUriFits ? { spotifyUri: sourceReq.spotifyUri } : {}),
      durationVerified: fits ? sourceReq.durationVerified || undefined : undefined,
      entrySource: 'imported' as const,
      matchVerified: false,
    });

    currentRequests.push(newReq);

    if (fits) {
      importedToQueue++;
    } else {
      importedToPending++;
      unmatchedRequestIds.push(newReq.id);
    }
  }

  return { importedToQueue, importedToPending, skippedDuplicates, total: sourceRequests.length, unmatchedRequestIds };
}

// ============================================
// MATCH FIX PROPAGATION
// ============================================

/**
 * After replacing a link in the library, propagate the new platformLink (and its song.link page) to
 * queued and pending songs in active/upcoming events that match this title+artist.
 * Returns the total count of updated requests.
 */
export async function propagateMatchFix(
  djUid: string,
  title: string,
  artist: string,
  newPlatformLink: string,
  songLinkUrl: string | null,
): Promise<number> {
  const { getEventsByDjId } = await import('./events');
  const allEvents = await getEventsByDjId(djUid);
  const activeEvents = allEvents.filter(e => e.status !== 'completed' && !e.archived);

  let updatedCount = 0;
  for (const event of activeEvents) {
    const mode = playModeOf(event);
    const requests = await getRequestsByEventId(event.id);
    for (const r of requests) {
      if (
        isFuzzySongMatch(title, artist, r.song.title, r.song.artist) &&
        (r.status === 'queued' || r.status === 'pending') &&
        canonicalLinkOrSame(r.platformLink ?? '') !== canonicalLinkOrSame(newPlatformLink) &&
        linkFitsPlayMode(newPlatformLink, r.platformName, mode)
      ) {
        const fields = matchedLinkFields({ platformLink: newPlatformLink, platformName: detectPlatformFromUrl(newPlatformLink) }, r.song);
        await updateRequest(r.id, {
          platformLink: fields.platformLink,
          platformName: fields.platformName,
          songLinkUrl: songLinkUrl ?? undefined,
          spotifyUri: fields.spotifyUri ?? r.spotifyUri,
          song: fields.song,
        });
        updatedCount++;
      }
    }
  }
  return updatedCount;
}

// ============================================
// VOTING
// ============================================

export type VoteType = 'up' | 'down';

export interface VoteStatus {
  voted: boolean;
  type?: VoteType;
}

// Deterministic vote document ID: ensures one vote per attendee per request
function getVoteDocId(eventId: string, requestId: string, attendeeId: string): string {
  return `${eventId}_${requestId}_${attendeeId}`;
}

export async function getVoteStatus(attendeeId: string, requestId: string): Promise<VoteStatus> {
  // Try deterministic ID first (new format)
  // We need the eventId, so query by oderId + requestId (works for both old and new)
  const q = query(
    collection(db, 'votes'),
    where('oderId', '==', attendeeId),
    where('requestId', '==', requestId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return { voted: false };
  const data = snapshot.docs[0].data();
  // Legacy votes without type field default to 'up'
  return { voted: true, type: data.type || 'up' };
}

export async function hasVoted(attendeeId: string, requestId: string): Promise<boolean> {
  return (await getVoteStatus(attendeeId, requestId)).voted;
}

export async function getVotersForRequest(requestId: string): Promise<Voter[]> {
  const q = query(collection(db, 'votes'), where('requestId', '==', requestId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    attendeeId: d.data().oderId || d.data().attendeeId,
    nickname: d.data().nickname || undefined,
  }));
}

export interface VoteBreakdownEntry {
  type: 'up' | 'down';
  votedAt: string | null;
  attendeeId: string;
}

// Client votes only: server auto-upvotes carry no `type` and aren't in upvoteCount either
export async function getVoteBreakdown(requestId: string): Promise<VoteBreakdownEntry[]> {
  const snapshot = await getDocs(query(collection(db, 'votes'), where('requestId', '==', requestId)));
  const entries: VoteBreakdownEntry[] = [];
  for (const d of snapshot.docs) {
    const data = d.data();
    if (data.type !== 'up' && data.type !== 'down') continue;
    entries.push({
      type: data.type,
      votedAt: data.votedAt instanceof Timestamp ? data.votedAt.toDate().toISOString()
        : typeof data.votedAt === 'string' ? data.votedAt : null,
      attendeeId: data.oderId || data.attendeeId,
    });
  }
  return entries;
}

export async function countParticipantsSince(eventId: string, since: Date): Promise<number> {
  const snapshot = await getCountFromServer(query(
    collection(db, 'eventParticipants'),
    where('eventId', '==', eventId),
    where('firstAt', '>=', Timestamp.fromDate(since)),
  ));
  return snapshot.data().count;
}

export async function getActiveParticipantIds(
  eventId: string
): Promise<{ id: string; nickname?: string }[]> {
  const participantMap = new Map<string, string | undefined>();

  const requestsSnap = await getDocs(
    query(collection(db, 'requests'), where('eventId', '==', eventId))
  );
  for (const d of requestsSnap.docs) {
    const data = d.data();
    const oderId = data.requestedBy?.oderId;
    if (oderId) {
      participantMap.set(oderId, data.requestedBy?.nickname || participantMap.get(oderId));
    }
  }

  const votesSnap = await getDocs(
    query(collection(db, 'votes'), where('eventId', '==', eventId))
  );
  for (const d of votesSnap.docs) {
    const data = d.data();
    const oderId = data.oderId;
    if (oderId) {
      participantMap.set(oderId, data.nickname || participantMap.get(oderId));
    }
  }

  // Get all rankers
  const rankingsSnap = await getDocs(
    query(collection(db, 'rankings'), where('eventId', '==', eventId))
  );
  for (const d of rankingsSnap.docs) {
    const data = d.data();
    const oderId = data.oderId || data.attendeeId;
    if (oderId && !participantMap.has(oderId)) {
      participantMap.set(oderId, undefined);
    }
  }

  return Array.from(participantMap.entries()).map(([id, nickname]) => ({ id, nickname }));
}

export async function getUniqueVoterCount(eventId: string): Promise<number> {
  const q = query(collection(db, 'votes'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  const uniqueVoters = new Set<string>();
  snapshot.docs.forEach(d => {
    const oderId = d.data().oderId;
    if (oderId) uniqueVoters.add(oderId);
  });
  return uniqueVoters.size;
}

export async function voteForRequest(
  attendeeId: string,
  requestId: string,
  voteType: VoteType = 'up'
): Promise<{ voteCount: number; hasVoted: boolean; voteType?: VoteType }> {
  const requestRef = doc(db, 'requests', requestId);
  const reqSnap = await getDoc(requestRef);
  const eventId = reqSnap.data()?.eventId;

  // Deterministic vote doc ID — enforces one vote per attendee per request
  const voteDocId = getVoteDocId(eventId, requestId, attendeeId);
  const voteRef = doc(db, 'votes', voteDocId);
  const existingSnap = await getDoc(voteRef);

  if (existingSnap.exists()) {
    const existingType = existingSnap.data().type || 'up';

    if (existingType === voteType) {
      // Same type — toggle off (remove vote)
      // Cloud Function onVoteDeleted will decrement the counts
      await firestoreDeleteDoc(voteRef);

      // Also clean up any legacy non-deterministic vote docs for this combo
      await cleanupLegacyVotes(attendeeId, requestId, voteDocId);

      // Read updated counts (may take a moment for Cloud Function to fire)
      const updatedSnap = await getDoc(requestRef);
      return { voteCount: updatedSnap.data()?.voteCount || 0, hasVoted: false };
    } else {
      // Different type — delete old, create new
      // Cloud Function: onVoteDeleted fires for old, onVoteCreated fires for new
      await firestoreDeleteDoc(voteRef);
      await setDoc(voteRef, {
        oderId: attendeeId,
        requestId,
        eventId,
        type: voteType,
        votedAt: serverTimestamp(),
      });

      const updatedSnap = await getDoc(requestRef);
      return { voteCount: updatedSnap.data()?.voteCount || 0, hasVoted: true, voteType };
    }
  } else {
    // No existing vote — create new with deterministic ID
    // Cloud Function onVoteCreated will increment the counts
    await setDoc(voteRef, {
      oderId: attendeeId,
      requestId,
      eventId,
      type: voteType,
      votedAt: serverTimestamp(),
    });

    // Also clean up any legacy non-deterministic vote docs for this combo
    await cleanupLegacyVotes(attendeeId, requestId, voteDocId);

    const updatedSnap = await getDoc(requestRef);
    return { voteCount: updatedSnap.data()?.voteCount || 0, hasVoted: true, voteType };
  }
}

// Clean up legacy vote documents that used random IDs (pre-security-fix)
async function cleanupLegacyVotes(attendeeId: string, requestId: string, deterministicId: string): Promise<void> {
  const q = query(
    collection(db, 'votes'),
    where('oderId', '==', attendeeId),
    where('requestId', '==', requestId)
  );
  const snapshot = await getDocs(q);
  for (const voteDoc of snapshot.docs) {
    // Only delete non-deterministic legacy docs
    if (voteDoc.id !== deterministicId) {
      try { await firestoreDeleteDoc(voteDoc.ref); } catch { /* ignore cleanup failures */ }
    }
  }
}

// ============================================
// TRENDING
// ============================================

export async function getRecentVoteCount(requestId: string): Promise<number> {
  const cutoff = new Date(Date.now() - TRENDING_WINDOW_MS);
  const q = query(
    collection(db, 'votes'),
    where('requestId', '==', requestId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter(d => {
    const votedAt = d.data().votedAt;
    if (!votedAt) return false;
    const date = votedAt instanceof Timestamp ? votedAt.toDate() : new Date(votedAt);
    return date > cutoff;
  }).length;
}

export async function isTrending(requestId: string): Promise<boolean> {
  const count = await getRecentVoteCount(requestId);
  return count >= TRENDING_VOTE_THRESHOLD;
}

export async function getTrendingRequests(eventId: string): Promise<SongRequest[]> {
  const requests = await getRequestsByEventId(eventId);
  const queued = requests.filter(r => r.status === 'queued');
  const trending: SongRequest[] = [];
  for (const req of queued) {
    if (await isTrending(req.id)) trending.push(req);
  }
  return trending;
}

// ============================================
// DUPLICATE DETECTION & COOLDOWNS
// ============================================

export async function hasDuplicateRequest(
  eventId: string,
  attendeeId: string,
  songTitle: string,
  songArtist: string,
  preloadedRequests?: SongRequest[]
): Promise<{ isDuplicate: boolean; existingRequest?: SongRequest }> {
  // When preloaded requests available, use them (no extra reads)
  const requests = preloadedRequests || await getRequestsByEventId(eventId);
  const existingRequest = requests.find(request => {
    if (request.requestedBy.oderId !== attendeeId) return false;
    if (request.status === 'overflow' || request.status === 'rejected') return false;
    return isFuzzySongMatch(songTitle, songArtist, request.song.title, request.song.artist);
  });
  return { isDuplicate: !!existingRequest, existingRequest };
}

export async function findExistingSongRequest(
  eventId: string,
  songTitle: string,
  songArtist: string,
  preloadedRequests?: SongRequest[]
): Promise<SongRequest | null> {
  const requests = preloadedRequests || await getRequestsByEventId(eventId);
  return requests.find(r => {
    if (r.status === 'overflow' || r.status === 'rejected') return false;
    if (r.status === 'pending' && r.isOverflow) return false;
    return isFuzzySongMatch(songTitle, songArtist, r.song.title, r.song.artist);
  }) || null;
}

export async function countRequestsByAttendee(eventId: string, attendeeId: string, preloadedRequests?: SongRequest[]): Promise<number> {
  if (preloadedRequests) {
    return preloadedRequests.filter(r =>
      r.requestedBy.oderId === attendeeId &&
      r.status !== 'rejected' &&
      r.status !== 'overflow' &&
      !r.superRequest
    ).length;
  }
  // Targeted query: only fetch this attendee's requests
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('requestedBy.oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter(d => {
    const data = d.data();
    return data.status !== 'rejected' && data.status !== 'overflow' && !data.superRequest;
  }).length;
}

export async function countRequestsByAttendeeAfter(eventId: string, attendeeId: string, afterTimestamp: string, preloadedRequests?: SongRequest[]): Promise<number> {
  const afterTime = new Date(afterTimestamp).getTime();
  if (preloadedRequests) {
    return preloadedRequests.filter(r =>
      r.requestedBy.oderId === attendeeId &&
      r.status !== 'rejected' &&
      r.status !== 'overflow' &&
      !r.superRequest &&
      new Date(r.createdAt).getTime() > afterTime
    ).length;
  }
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('requestedBy.oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter(d => {
    const data = d.data();
    return data.status !== 'rejected' && data.status !== 'overflow' && !data.superRequest &&
      new Date(data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt).getTime() > afterTime;
  }).length;
}

export async function countNominationsByAttendee(eventId: string, attendeeId: string, preloadedRequests?: SongRequest[]): Promise<number> {
  if (preloadedRequests) {
    return preloadedRequests.filter(r =>
      r.requestedBy.oderId === attendeeId &&
      r.superRequest === true &&
      r.status !== 'rejected'
    ).length;
  }
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('requestedBy.oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.filter(d => {
    const data = d.data();
    return data.superRequest === true && data.status !== 'rejected';
  }).length;
}

// ============================================
// OVERFLOW POOL
// ============================================

export function getOverflowByAttendee(eventId: string, attendeeId: string, preloadedRequests?: SongRequest[]): SongRequest[] {
  const requests = preloadedRequests || [];
  return requests
    .filter(r =>
      r.eventId === eventId &&
      r.requestedBy.oderId === attendeeId &&
      (r.status === 'overflow' || (r.status === 'pending' && r.isOverflow))
    )
    .sort((a, b) => (a.overflowPriority ?? 999) - (b.overflowPriority ?? 999));
}

export function countOverflowByAttendee(eventId: string, attendeeId: string, preloadedRequests?: SongRequest[]): number {
  const requests = preloadedRequests || [];
  return requests.filter(r =>
    r.eventId === eventId &&
    r.requestedBy.oderId === attendeeId &&
    (r.status === 'overflow' || (r.status === 'pending' && r.isOverflow))
  ).length;
}

export async function updateOverflowPriority(requestId: string, newPriority: number): Promise<void> {
  const ref = doc(db, 'requests', requestId);
  await updateDoc(ref, { overflowPriority: newPriority, updatedAt: serverTimestamp() });
}

export async function reorderOverflow(requestIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  requestIds.forEach((id, index) => {
    batch.update(doc(db, 'requests', id), { overflowPriority: index, updatedAt: serverTimestamp() });
  });
  await batch.commit();
}

export async function getLastRequestTime(eventId: string, attendeeId: string, preloadedRequests?: SongRequest[]): Promise<Date | null> {
  if (preloadedRequests) {
    const attendeeRequests = preloadedRequests
      .filter(r => r.requestedBy.oderId === attendeeId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (attendeeRequests.length === 0) return null;
    return new Date(attendeeRequests[0].createdAt);
  }
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('requestedBy.oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const times = snapshot.docs.map(d => {
    const data = d.data();
    return new Date(data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt).getTime();
  });
  return new Date(Math.max(...times));
}

export async function isInCooldown(eventId: string, attendeeId: string, cooldownMinutes: number, preloadedRequests?: SongRequest[]): Promise<{ inCooldown: boolean; remainingSeconds: number }> {
  const lastRequestTime = await getLastRequestTime(eventId, attendeeId, preloadedRequests);
  if (!lastRequestTime) return { inCooldown: false, remainingSeconds: 0 };

  const cooldownEndTime = new Date(lastRequestTime.getTime() + cooldownMinutes * 60 * 1000);
  const remainingMs = cooldownEndTime.getTime() - Date.now();

  if (remainingMs <= 0) return { inCooldown: false, remainingSeconds: 0 };
  return { inCooldown: true, remainingSeconds: Math.ceil(remainingMs / 1000) };
}

// ============================================
// MANUAL ORDERING
// ============================================

export async function updateManualOrder(
  requestOrders: { requestId: string; manualOrder: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  for (const { requestId, manualOrder } of requestOrders) {
    batch.update(doc(db, 'requests', requestId), {
      manualOrder,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

// ============================================
// RANKED CHOICE VOTING (Copeland Method)
// ============================================

export async function getRankingsByEventId(eventId: string): Promise<AttendeeRanking[]> {
  const q = query(collection(db, 'rankings'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  // Deduplicate by attendeeId (keep most recent) to handle legacy duplicate docs
  const byAttendee = new Map<string, AttendeeRanking>();
  for (const d of snapshot.docs) {
    const data = d.data();
    const attendeeId = data.oderId || data.attendeeId;
    const updatedAt = data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : data.updatedAt;
    const existing = byAttendee.get(attendeeId);
    if (!existing || updatedAt > existing.updatedAt) {
      byAttendee.set(attendeeId, {
        attendeeId,
        eventId: data.eventId,
        rankings: data.rankings,
        fullOrder: Array.isArray(data.fullOrder) ? data.fullOrder : undefined,
        sortedThroughId: typeof data.sortedThroughId === 'string' ? data.sortedThroughId : null,
        updatedAt,
      });
    }
  }
  return Array.from(byAttendee.values());
}

export async function getAttendeeRanking(eventId: string, attendeeId: string): Promise<AttendeeRanking | null> {
  // Try deterministic doc ID first (new format)
  const docId = `${eventId}_${attendeeId}`;
  const snap = await getDoc(doc(db, 'rankings', docId));
  if (snap.exists()) {
    const data = snap.data();
    return {
      attendeeId: data.oderId || data.attendeeId,
      eventId: data.eventId,
      rankings: data.rankings,
      fullOrder: Array.isArray(data.fullOrder) ? data.fullOrder : undefined,
      sortedThroughId: typeof data.sortedThroughId === 'string' ? data.sortedThroughId : null,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
    };
  }
  // Fallback: query for legacy non-deterministic docs
  const q = query(
    collection(db, 'rankings'),
    where('eventId', '==', eventId),
    where('oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const data = snapshot.docs[0].data();
  return {
    attendeeId: data.oderId || data.attendeeId,
    eventId: data.eventId,
    rankings: data.rankings,
    fullOrder: Array.isArray(data.fullOrder) ? data.fullOrder : undefined,
    sortedThroughId: typeof data.sortedThroughId === 'string' ? data.sortedThroughId : null,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
}

export interface RankingSave {
  rankings: string[];
  fullOrder: string[];
  sortedThroughId: string | null;
}

const legacyRankingsCleaned = new Set<string>();

export async function submitRanking(
  eventId: string,
  attendeeId: string,
  save: RankingSave
): Promise<AttendeeRanking> {
  const docId = `${eventId}_${attendeeId}`;
  const rankingRef = doc(db, 'rankings', docId);

  // Clean up legacy non-deterministic docs once per page session (migration from query-based to deterministic IDs)
  if (!legacyRankingsCleaned.has(docId)) {
    const legacyQuery = query(
      collection(db, 'rankings'),
      where('eventId', '==', eventId),
      where('oderId', '==', attendeeId)
    );
    const legacyDocs = await getDocs(legacyQuery);
    const batch = writeBatch(db);
    let deletions = 0;
    legacyDocs.docs.forEach(d => {
      if (d.id !== docId) {
        batch.delete(d.ref);
        deletions++;
      }
    });
    if (deletions > 0) await batch.commit();
    legacyRankingsCleaned.add(docId);
  }

  await setDoc(rankingRef, {
    eventId,
    oderId: attendeeId,
    rankings: save.rankings,
    fullOrder: save.fullOrder,
    sortedThroughId: save.sortedThroughId,
    updatedAt: serverTimestamp(),
  });

  return { attendeeId, eventId, ...save, updatedAt: new Date().toISOString() };
}

export async function calculateCopelandScores(
  eventId: string,
  rankSize: number,
  mode: 'consensus' | 'discovery' = 'consensus',
  excludeAttendeeIds?: Set<string>
): Promise<Map<string, number>> {
  const requests = (await getRequestsByEventId(eventId)).filter(r => r.status === 'queued');
  const queuedIds = new Set(requests.map(r => r.id));
  const rankings = (await getRankingsByEventId(eventId))
    .filter(r => !excludeAttendeeIds?.has(r.attendeeId))
    .map(r => ({ ...r, rankings: effectiveBallot(r, queuedIds, rankSize) }));

  const scores = new Map<string, number>();
  requests.forEach(r => scores.set(r.id, 0));

  if (rankings.length === 0 || requests.length < 2) return scores;

  const requestIds = requests.map(r => r.id);

  for (let i = 0; i < requestIds.length; i++) {
    for (let j = i + 1; j < requestIds.length; j++) {
      const candidateA = requestIds[i];
      const candidateB = requestIds[j];
      let winsA = 0, winsB = 0;

      for (const ranking of rankings) {
        const posA = ranking.rankings.indexOf(candidateA);
        const posB = ranking.rankings.indexOf(candidateB);

        if (posA >= 0 && posB >= 0) {
          if (posA < posB) winsA++;
          else if (posB < posA) winsB++;
        } else if (mode === 'consensus') {
          if (posA >= 0 && posB < 0) winsA++;
          else if (posB >= 0 && posA < 0) winsB++;
        }
      }

      if (winsA > winsB) {
        scores.set(candidateA, (scores.get(candidateA) || 0) + 1);
        scores.set(candidateB, (scores.get(candidateB) || 0) - 1);
      } else if (winsB > winsA) {
        scores.set(candidateB, (scores.get(candidateB) || 0) + 1);
        scores.set(candidateA, (scores.get(candidateA) || 0) - 1);
      }
    }
  }

  return scores;
}

export async function calculateBothCopelandScores(
  eventId: string,
  rankSize: number,
  excludeAttendeeIds?: Set<string>
): Promise<{ consensus: Map<string, number>; discovery: Map<string, number> }> {
  return {
    consensus: await calculateCopelandScores(eventId, rankSize, 'consensus', excludeAttendeeIds),
    discovery: await calculateCopelandScores(eventId, rankSize, 'discovery', excludeAttendeeIds),
  };
}

export async function findHiddenGems(eventId: string, rankSize: number, minRankDelta: number = 2): Promise<Array<{ requestId: string; consensusRank: number; discoveryRank: number; delta: number }>> {
  const consensusScores = await calculateCopelandScores(eventId, rankSize, 'consensus');
  const discoveryScores = await calculateCopelandScores(eventId, rankSize, 'discovery');

  const consensusSorted = [...consensusScores.entries()].sort((a, b) => b[1] - a[1]);
  const discoverySorted = [...discoveryScores.entries()].sort((a, b) => b[1] - a[1]);

  const consensusRanks = new Map<string, number>();
  const discoveryRanks = new Map<string, number>();
  consensusSorted.forEach(([id], index) => consensusRanks.set(id, index + 1));
  discoverySorted.forEach(([id], index) => discoveryRanks.set(id, index + 1));

  const hiddenGems: Array<{ requestId: string; consensusRank: number; discoveryRank: number; delta: number }> = [];

  for (const [requestId, consensusRank] of consensusRanks) {
    const discoveryRank = discoveryRanks.get(requestId) || 0;
    const delta = consensusRank - discoveryRank;
    if (delta >= minRankDelta) {
      hiddenGems.push({ requestId, consensusRank, discoveryRank, delta });
    }
  }

  return hiddenGems.sort((a, b) => b.delta - a.delta);
}

export async function deleteRankingsByEventId(eventId: string): Promise<void> {
  const q = query(collection(db, 'rankings'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}

// ============================================
// SEEN SONGS (New Song Indicators)
// ============================================

export async function getSeenSongs(eventId: string, attendeeId: string): Promise<Set<string>> {
  const q = query(
    collection(db, 'seenSongs'),
    where('eventId', '==', eventId),
    where('oderId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  const seen = new Set<string>();
  snapshot.docs.forEach(d => {
    const requestId = d.data().requestId;
    if (requestId) seen.add(requestId);
  });
  return seen;
}

export async function markSongAsSeen(eventId: string, attendeeId: string, requestId: string): Promise<void> {
  // Check if already marked
  const q = query(
    collection(db, 'seenSongs'),
    where('eventId', '==', eventId),
    where('oderId', '==', attendeeId),
    where('requestId', '==', requestId)
  );
  const existing = await getDocs(q);
  if (!existing.empty) return;

  await addDoc(collection(db, 'seenSongs'), {
    eventId,
    oderId: attendeeId,
    requestId,
    seenAt: serverTimestamp(),
  });
}

// ============================================
// STALENESS SAFEGUARD
// ============================================

// Get all seen songs for an event (bulk query, returns Map<attendeeId, Set<requestId>>)
export async function getAllSeenSongsByEvent(eventId: string): Promise<Map<string, Set<string>>> {
  const q = query(collection(db, 'seenSongs'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  const result = new Map<string, Set<string>>();
  snapshot.docs.forEach(d => {
    const data = d.data();
    const attendeeId = data.oderId;
    const requestId = data.requestId;
    if (!attendeeId || !requestId) return;
    if (!result.has(attendeeId)) result.set(attendeeId, new Set());
    result.get(attendeeId)!.add(requestId);
  });
  return result;
}

// Find attendees whose rankings should be withheld due to staleness
export async function getStaleAttendeeIds(
  eventId: string,
  stalenessMinutes: number,
  queuedRequests: SongRequest[]
): Promise<Set<string>> {
  if (stalenessMinutes <= 0) return new Set();

  const cutoffMs = Date.now() - stalenessMinutes * 60 * 1000;

  // Find queued songs approved before the cutoff
  const staleCandidateIds = new Set<string>();
  for (const req of queuedRequests) {
    const approvedTime = req.timestamps.approved || req.createdAt;
    if (new Date(approvedTime).getTime() < cutoffMs) {
      staleCandidateIds.add(req.id);
    }
  }

  if (staleCandidateIds.size === 0) return new Set();

  // Load all seen songs and rankings for the event
  const allSeen = await getAllSeenSongsByEvent(eventId);
  const rankings = await getRankingsByEventId(eventId);

  const staleAttendees = new Set<string>();

  for (const ranking of rankings) {
    const attendeeSeen = allSeen.get(ranking.attendeeId) || new Set();
    // If ANY stale-candidate song is NOT in their seen set → stale
    for (const candidateId of staleCandidateIds) {
      if (!attendeeSeen.has(candidateId)) {
        staleAttendees.add(ranking.attendeeId);
        break;
      }
    }
  }

  return staleAttendees;
}

// Convenience wrapper: calculate both Copeland scores with staleness filtering
// Accepts optional pre-loaded requests to avoid redundant Firestore reads
export async function calculateBothCopelandScoresWithStaleness(
  eventId: string,
  stalenessMinutes: number,
  rankSize: number,
  preloadedRequests?: SongRequest[]
): Promise<{ consensus: Map<string, number>; discovery: Map<string, number>; staleCount: number }> {
  const requests = preloadedRequests
    ? preloadedRequests.filter(r => r.status === 'queued')
    : (await getRequestsByEventId(eventId)).filter(r => r.status === 'queued');
  const staleIds = await getStaleAttendeeIds(eventId, stalenessMinutes, requests);
  const { consensus, discovery } = await calculateBothCopelandScores(eventId, rankSize, staleIds);
  return { consensus, discovery, staleCount: staleIds.size };
}

// ============================================
// CACHED COPELAND SCORES (server-side computed)
// ============================================

export interface CachedCopelandScores {
  consensus: Map<string, number>;
  discovery: Map<string, number>;
  staleCount: number;
  totalRankings: number;
  uniqueVoters: number;
  averageRankedItems: number;
  uniqueSongsRanked: number;
}

/**
 * Subscribe to server-cached Copeland scores for an event.
 * The server recomputes scores whenever a ranking is created/updated/deleted.
 * This replaces the 30-second polling of calculateBothCopelandScoresWithStaleness.
 * Cost: 1 read for initial snapshot, then delta reads only.
 */
export function subscribeToCachedCopelandScores(
  eventId: string,
  callback: (scores: CachedCopelandScores | null) => void
): Unsubscribe {
  const docRef = doc(db, 'rankingScores', eventId);
  return onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    const data = snapshot.data();
    const consensus = new Map<string, number>(Object.entries(data.consensus || {}));
    const discovery = new Map<string, number>(Object.entries(data.discovery || {}));
    callback({
      consensus,
      discovery,
      staleCount: data.staleCount || 0,
      totalRankings: data.totalRankings || 0,
      uniqueVoters: data.uniqueVoters || 0,
      averageRankedItems: data.averageRankedItems || 0,
      uniqueSongsRanked: data.uniqueSongsRanked || 0,
    });
  });
}

// F18: Get played requests for an event (ordered by finishedPlaying time)
export async function getPlayedRequests(eventId: string): Promise<SongRequest[]> {
  const all = await getRequestsByEventId(eventId);
  return all
    .filter(r => r.status === 'played')
    .sort((a, b) => {
      const aTime = a.timestamps?.finishedPlaying || a.timestamps?.startedPlaying || a.createdAt;
      const bTime = b.timestamps?.finishedPlaying || b.timestamps?.startedPlaying || b.createdAt;
      return new Date(aTime).getTime() - new Date(bTime).getTime();
    });
}

// F18: Get unplayed queued requests for an event
export async function getUnplayedRequests(eventId: string): Promise<SongRequest[]> {
  const all = await getRequestsByEventId(eventId);
  return all.filter(r => r.status === 'queued' || r.status === 'pending');
}

// Super Request: Count unique attendees who nominated the same song (by title+artist fuzzy match)
export async function getSuperRequestCount(
  eventId: string,
  title: string,
  artist: string
): Promise<{ count: number; requestIds: string[] }> {
  const all = await getRequestsByEventId(eventId);
  const matching = all.filter(r =>
    r.superRequest &&
    (r.status === 'pending' || r.status === 'queued') &&
    isFuzzySongMatch(r.song.title, r.song.artist, title, artist)
  );
  // Count unique attendees
  const uniqueAttendees = new Set(matching.map(r => r.requestedBy.oderId));
  return { count: uniqueAttendees.size, requestIds: matching.map(r => r.id) };
}

// Super Request: Auto-approve if threshold met; merge duplicate nominations
export async function checkAndApproveSuperRequest(
  eventId: string,
  title: string,
  artist: string,
  threshold: number,
  totalAttendees: number,
  thresholdType: 'count' | 'percentage'
): Promise<{ approved: boolean; count: number }> {
  const { requestIds } = await getSuperRequestCount(eventId, title, artist);
  const groupKey = makePPGroupKey(title, artist);
  const { count } = await getUpvotesForGroup(eventId, groupKey);
  const effectiveThreshold = thresholdType === 'percentage'
    ? Math.max(2, Math.ceil((threshold / 100) * totalAttendees))
    : threshold;

  if (count >= effectiveThreshold && requestIds.length > 0) {
    // Auto-approve the first matching request, mark the rest as merged
    const [primaryId, ...duplicateIds] = requestIds;
    await updateRequest(primaryId, {
      status: 'queued',
      superRequest: true,
      superRequestCount: count,
      timestamps: {
        requested: new Date().toISOString(),
        approved: new Date().toISOString(),
      },
    });
    // Mark duplicates as rejected (merged)
    for (const dupId of duplicateIds) {
      await updateRequest(dupId, {
        status: 'rejected',
        rejectionReason: 'Merged into The People\'s Pick',
      });
    }
    return { approved: true, count };
  }
  return { approved: false, count };
}

// DJ manual approval of a nomination group (bypasses threshold requirement)
export async function approveSuperRequestManually(
  eventId: string,
  title: string,
  artist: string,
  coApprovalEnabled = false
): Promise<{ primaryId: string; mergedCount: number }> {
  const all = await getRequestsByEventId(eventId);
  const nominations = all.filter(r =>
    r.superRequest &&
    (r.status === 'pending' || r.status === 'queued') &&
    isFuzzySongMatch(r.song.title, r.song.artist, title, artist)
  );
  if (nominations.length === 0) throw new Error('No nominations found for this song');

  const matching = coApprovalEnabled
    ? nominations.filter(r =>
        r.poolApproved === true &&
        r.coApprovalStatus !== 'pending' &&
        r.coApprovalStatus !== 'rejected'
      )
    : nominations;
  if (matching.length === 0) throw new Error('This nomination is waiting on the co-approver');

  const uniqueAttendees = new Set(matching.map(r => r.requestedBy.oderId));
  const [primary, ...duplicates] = matching;

  await updateRequest(primary.id, {
    status: 'queued',
    superRequest: true,
    superRequestCount: uniqueAttendees.size,
    timestamps: {
      requested: primary.timestamps?.requested || new Date().toISOString(),
      approved: new Date().toISOString(),
    },
  });

  for (const dup of duplicates) {
    await updateRequest(dup.id, {
      status: 'rejected',
      rejectionReason: 'Merged into The People\'s Pick',
    });
  }

  return { primaryId: primary.id, mergedCount: duplicates.length };
}

// Pool approval: DJ marks a nomination as content-safe and moves it to the pool.
// Does NOT graduate to queue — upvotes must reach the threshold for that.
export async function approveNominationToPool(
  eventId: string,
  title: string,
  artist: string
): Promise<void> {
  const all = await getRequestsByEventId(eventId);
  const matching = all.filter(r =>
    r.superRequest &&
    r.status === 'pending' &&
    !r.poolApproved &&
    isFuzzySongMatch(r.song.title, r.song.artist, title, artist)
  );
  if (matching.length === 0) throw new Error('No pending nominations found for this song');
  for (const req of matching) {
    await updateRequest(req.id, { poolApproved: true });
  }
}
